/**
 * This is the entrypoint for importing a route file.
 */

import { GetPlaceRequest, GetPlaceResponse, CoordinateStringPair } from '@edge-functions/api';
import { processKmzFile } from './import-kmz';
import { metersToMiles, requestPersistedStorage, setStatus, sortSpots } from './import-utils';
import { assert } from "../util/assert";
import * as db from '..//util/db';
import { findClosestPoints, computeMileMarker } from '../util/gps';
import { RouteData, Destination, RouteArray, getRouteImageUrl, RouteImageFiles, RouteAndImages, Coordinate } from '../util/route-data';
import { ThrottledPromiseQueue, THROTTLED } from '../util/throttled_promise_queue';
import { areArraysEqual } from '../util/types';
import { clearRouteAssociatedStorageValues } from '../util/local-storage';
import { isGpxFile, processGpxFile } from './import-gpx';
import { getGoogleMyMapsUrlMid } from './route-refresh-support';


// The Maps API calls are really slow, so I only test it when I need it.
const ENABLE_MAPS_API_CALLS = true; // location.hostname !== 'localhost';

/**
 * Imports route data into browser storage.
 */
export async function importFile(file: File | ArrayBuffer, { docLink, filename, refreshFrom }: { docLink?: string, filename?: string, refreshFrom?: RouteData } = {}): Promise<void> {
  await requestPersistedStorage();

  if (!filename && file instanceof File) {
    filename = file.name;
  }
  let routeAndImages: RouteAndImages;
  if (await isGpxFile(file, filename)) {
    routeAndImages = await processGpxFile(file, { docLink });
  } else {
    routeAndImages = await processKmzFile(file, { docLink, filename });
  }

  await fillWithGoogleMapsData(routeAndImages.routeData, refreshFrom);
  sortSpots(routeAndImages.routeData);
  await saveRoute(routeAndImages);
}

export async function refreshRoute(routeData: RouteData): Promise<void> {
  assert(routeData?.link);
  const link = routeData.link;
  await importFromUrl(link, { refreshFrom: routeData });
}

/** Imports from a URL that contains a supported route file. */
export async function importFromUrl(urlString: string, { refreshFrom }: { refreshFrom?: RouteData } = {}): Promise<void> {
  if (!navigator.onLine) {
    throw 'Your device is offline.';
  }
  let url: URL;
  try {
    url = new URL(urlString);
  } catch {
    throw 'Invalid URL';
  }
  const googleMyMapsMid = getGoogleMyMapsUrlMid(url);
  const fileUrlString = googleMyMapsMid ? ('https://www.google.com/maps/d/kml?mid=' + googleMyMapsMid) : urlString;
  let response: Response;
  try {
    response = await fetch(fileUrlString);
  } catch (e: any) {
    throw 'Request failed.';
  }
  if (!response.ok) {
    let statusText = response.statusText || getStatusText(response.status);
    throw `${response.status} ${statusText}`;
  }
  const contents = await response.arrayBuffer();
  if (contents.byteLength < 10) {
    throw `The request failed for an unknown reason.`;
  }
  await importFile(contents, { docLink: urlString, refreshFrom });
}

/** Saves a route to the browser storage. */
export async function saveRoute({ routeData, images }: RouteAndImages) {
  await db.set(routeData);
  if (images) {
    setStatus('Saving images');
    await saveImages(images);
  }
  clearRouteAssociatedStorageValues();
}

async function saveImages(images: RouteImageFiles): Promise<void> {
  navigator.serviceWorker.controller!.postMessage({
    command: 'clearImageCache'
  });
  for (const [imagePath, fileBuffer] of Object.entries(images)) {
    navigator.serviceWorker.controller!.postMessage(
      {
        'command': 'saveImage',
        'path': getRouteImageUrl(imagePath),
        'fileContent': fileBuffer
      }, [fileBuffer]);
  }
}

/**
 * 
 * @param routeData 
 * @param prevRouteData Should only be set when refreshing an existing route.
 */
async function fillWithGoogleMapsData(routeData: RouteData, prevRouteData?: RouteData): Promise<void> {
  if (!ENABLE_MAPS_API_CALLS) {
    return;
  }
  const isSameRoute =
    prevRouteData ? areArraysEqual(routeData.route, prevRouteData.route) : false;
  const prevSpots = prevRouteData ? prevRouteData.spots : null;
  const queue = new ThrottledPromiseQueue(6);
  for (const spot of routeData.spots) {
    if (spot.isRouteStep) {
      continue;
    }
    // Set the place id.
    if (prevSpots) {
      const prevSpot = findSameSpot(spot, prevSpots);
      if (prevSpot) {
        if (prevSpot.googlePlaceId) {
          spot.googlePlaceId = prevSpot.googlePlaceId;
        }
        if (isSameRoute && prevSpot.exitCoord) {
          spot.exitCoord = prevSpot.exitCoord;
          spot.milesFromExit = prevSpot.milesFromExit;
        }
      }
    }
    if (!spot.googlePlaceId || !spot.exitCoord) {
      queue.add(updateFromQueryPlace.bind(undefined, routeData.route, spot, queue));
    }
  }

  setGoogleMapsStatus(queue);

  try {
    await queue.run();
  } catch (e: any) {
    // Importing PlaceIds is optional. If this crashes, just quietly continue.
    console.error(e);
  }
}

interface PlaceQueryResults {
  id: string;
  permanentlyClosed: boolean;
}

async function updateFromQueryPlace(route: RouteArray, spot: Destination, queue: ThrottledPromiseQueue): Promise<void> {
  const response = await queryPlace(spot, route);
  if (response.googlePlaceId) {
    spot.googlePlaceId = response.googlePlaceId;
    if (spot.permanentlyClosed) {
      spot.permanentlyClosed = true;
    } else {
      delete spot.permanentlyClosed;
    }
  }
  if (response.routeExit) {
    spot.exitCoord = response.routeExit;
    spot.mile = computeMileMarker(route, response.routeExit);
    spot.milesFromExit = metersToMiles(response.metersFromExit);
  }
  setGoogleMapsStatus(queue);
}

async function queryPlace(spot: Destination, route: RouteArray): Promise<GetPlaceResponse> {
  const [routeIndex] = findClosestPoints(route, spot.coord);
  const request: GetPlaceRequest = {
    routeCoord: `${route[routeIndex]},${route[routeIndex + 1]}`,
    placeCoord: toCoordinateStringPair(spot.coord),
    placeName: spot.name,
  }
  const url = `/api/query-place?${new URLSearchParams(request).toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 429) {
      throw THROTTLED;
    } else {
      throw new Error(`${response.status} - ${response.statusText}`);
    }
  }
  return await response.json() as GetPlaceResponse;
}

function toCoordinateStringPair(coord: Coordinate): CoordinateStringPair {
  return `${coord.lat},${coord.lon}`;
}

function setGoogleMapsStatus(queue: ThrottledPromiseQueue): void {
  setStatus('Querying Google Maps API (' + queue.numComplete +
    ' of ' + queue.numTotal + ')');
}

function findSameSpot(spot: Destination, otherSpots: Destination[]): Destination | null {
  for (const otherSpot of otherSpots) {
    if (spot.coord.lat === otherSpot.coord.lat &&
      spot.coord.lon === otherSpot.coord.lon) {
      return otherSpot;
    }
  }
  return null;
}

function getStatusText(code: number): string {
  switch (code) {
    case 400: return 'Bad Request';
    case 401: return 'Unauthorized';
    case 403: return 'Payment Required';
    case 404: return 'Not Found';
    case 408: return 'Request Timeout';
    case 500: return 'Internal Server Error';
    case 501: return 'Not Implemented';
    case 502: return 'Bad Gateway';
    case 503: return 'Service Unavailable';
    case 504: return 'Gateway Timeout';
    default: return '';
  }
}